import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Row, Input, Button, Dropdown, message } from "antd";
import type { MenuProps } from "antd";
import { useDebounce } from "@react-hook/debounce";
import { HiSearch } from "react-icons/hi";

import ScrollToTopButton from "components/scroll-to-top-button/scroll-to-top-button";
import GuideCard from "components/guide-card/guide-card";

import { useGetGuidesQuery } from "graphql/queries/get-guides.generated";
import type { Guides, Guides_Bool_Exp } from "graphql/generated/graphql";
import { useProcessingGuidesSubscription } from "graphql/subscriptions/processing-guides.generated";
import { createLabelForFilters, voidFnc } from "utils/helpers";
import { buildGuidesVisibilityFilter } from "utils/permissions-management";
import { useLoggedUser } from "state/reactive-vars/logged-user-var";
import { useGuideFiltersStore } from "state/zustand-stores/guide-filters-store";
import routeNames from "constants/route-names";
import { GuideStatus, UserRoleValue } from "constants/field-values";
import { SEARCH_DEBOUNCE_DELAY_MS } from "constants/constants";

const GuidesScreen: React.FC = () => {
	const scrollContainerRef = useRef<HTMLDivElement>(null);
	const navigate = useNavigate();
	const { loggedUserValue, isUserAnon } = useLoggedUser();

	const [propertiesDropdownOpen, setPropertiesDropdownOpen] = useState<boolean>(false);
	const [categoriesDropdownOpen, setCategoriesDropdownOpen] = useState<boolean>(false);
	const [guides, setGuides] = useState<Guides[]>([]);
	const [productCategories, setProductCategories] = useState<Map<string, string>>();
	const [processingGuideIds, setProcessingGuideIds] = useState<string[]>([]);
	const [messageApi, contextHolder] = message.useMessage();

	const selectedPropertiesFilter = useGuideFiltersStore(state => state.filters.selectedPropertiesFilter);
	const selectedCategoriesFilter = useGuideFiltersStore(state => state.filters.selectedCategoriesFilter);

	const setPropertyFilters = useGuideFiltersStore(state => state.setPropertyFilters);
	const setCategoryFilters = useGuideFiltersStore(state => state.setCategoryFilters);
	const searchTerm = useGuideFiltersStore(state => state.filters.searchGuideTerm);

	const setSearchTerm = useGuideFiltersStore(state => state.setSearchTerm);

	const [debouncedSearchTerm, setDebouncedSearchTerm] = useDebounce(searchTerm, SEARCH_DEBOUNCE_DELAY_MS);

	useEffect(() => {
		setDebouncedSearchTerm(searchTerm);
	}, [searchTerm]);

	const handleSearchTermChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			// Directly update the search term in Zustand
			setSearchTerm(event.target.value);
		},
		[setSearchTerm]
	);

	const handleOnSelectPropertiesFilter = useCallback(
		args => {
			const { selectedKeys } = args;

			if (selectedKeys.includes("property-dropdown-option-all")) {
				setPropertyFilters([]);
				return;
			}

			setPropertyFilters(selectedKeys);
		},
		[setPropertyFilters]
	);

	const handleOnSelectCategoriesFilter = useCallback(
		args => {
			const { selectedKeys } = args;

			if (selectedKeys.includes("category-dropdown-option-all")) {
				setCategoryFilters([]);
				return;
			}

			setCategoryFilters(selectedKeys);
		},
		[setCategoryFilters]
	);

	const handleOnCardClick = useCallback(
		(guideId, guideStatus) => {
			if (guideStatus === GuideStatus.draft || guideStatus === GuideStatus.processing) {
				navigate(routeNames.GUIDE_DETAILS_EDIT.replace(":guideId", guideId));
				return;
			}

			navigate(routeNames.GUIDE_DETAILS.replace(":guideId", guideId));
		},
		[navigate]
	);

	const guidesFilter = useMemo(() => {
		// Every condition is collected into a single `_and` so that the dropdowns, the search box
		// and the visibility rules narrow the list together instead of replacing one another.
		const conditions: Guides_Bool_Exp[] = [
			{
				status: {
					_in: [GuideStatus.published, GuideStatus.draft, GuideStatus.processing]
				}
			},
			{
				_or: [{ handover: { status: { _eq: "PUBLISHED" } } }, { handover_id: { _is_null: true } }]
			}
		];

		const visibilityFilter = buildGuidesVisibilityFilter(loggedUserValue);

		if (visibilityFilter) {
			conditions.push(visibilityFilter);
		}

		// only add db filter if the user selected SOME properties
		if (
			selectedPropertiesFilter.length !== 0 &&
			selectedPropertiesFilter.length !== loggedUserValue.user_properties.length
		) {
			conditions.push({
				product_guides: {
					product: {
						installed_products: {
							property_id: { _in: selectedPropertiesFilter }
						}
					}
				}
			});
		}

		if (selectedCategoriesFilter.length) {
			conditions.push({
				product_guides: {
					product: { type: { _in: selectedCategoriesFilter } }
				}
			});
		}

		if (debouncedSearchTerm) {
			conditions.push({
				_or: [
					{ title: { _ilike: `%${debouncedSearchTerm}%` } },
					{ description: { _ilike: `%${debouncedSearchTerm}%` } },
					{
						product_guides: {
							product: {
								name: { _ilike: `%${debouncedSearchTerm}%` },
								model: { _ilike: `%${debouncedSearchTerm}%` },
								make: { _ilike: `%${debouncedSearchTerm}%` }
							}
						}
					}
				]
			});
		}

		return { _and: conditions };
	}, [loggedUserValue, selectedPropertiesFilter, selectedCategoriesFilter, debouncedSearchTerm]);

	const { loading } = useGetGuidesQuery({
		// A visitor without an account gets no listing; they reach a guide only through a shared link.
		skip: isUserAnon,
		variables: {
			where: guidesFilter
		},
		onCompleted: ({ guides, product_categories }) => {
			const filteredGuides = guides.filter(g => {
				if (g.status === GuideStatus.draft || g.status === GuideStatus.processing) {
					if (
						loggedUserValue.role === UserRoleValue.admin_user ||
						g.created_by_user_id === loggedUserValue.id ||
						(loggedUserValue.role === UserRoleValue.vendor_manager &&
							loggedUserValue.organization_id === g.organization_id)
					) {
						return true;
					}
					return false;
				}
				return true;
			});

			setGuides(filteredGuides as Guides[]);
			const productCategoryMap = new Map();
			product_categories.forEach(v => {
				productCategoryMap.set(v.name, v.label);
			});

			setProductCategories(productCategoryMap);

			// Track processing guides IDs for subscription
			const processingIds = filteredGuides
				.filter(guide => guide.status === GuideStatus.processing)
				.map(guide => guide.id);
			setProcessingGuideIds(processingIds);
		}
	});

	const showToast = useCallback(
		(guideId: string) => {
			messageApi.open({
				className: "guide-draft-toast",
				icon: null,
				content: (
					<div>
						<img src={`${process.env.PUBLIC_URL}/assets/images/icons/checkmark.svg`} alt="Success" />
						Your draft Guide is ready. Click here to review and publish!
					</div>
				),
				duration: 5000,
				onClick: () => {
					navigate(routeNames.GUIDE_DETAILS_EDIT.replace(":guideId", guideId));
				}
			});
		},
		[messageApi, navigate]
	);

	// Single subscription for all processing guides
	useProcessingGuidesSubscription({
		skip: processingGuideIds.length === 0,
		variables: {
			guideIds: processingGuideIds
		},
		onData: ({ data }) => {
			const updatedGuides = data.data?.guides || [];

			// Check for guides that changed from processing to draft
			updatedGuides.forEach(updatedGuide => {
				const currentGuide = guides.find(g => g.id === updatedGuide.id);

				if (currentGuide?.status === GuideStatus.processing && updatedGuide.status === GuideStatus.draft) {
					console.log(`Guide ${updatedGuide.id} processing completed, updating state...`);

					// Update the guides state
					setGuides(
						prevGuides => prevGuides.map(guide => (guide.id === updatedGuide.id ? updatedGuide : guide)) as Guides[]
					);

					// Remove from processing list
					setProcessingGuideIds(prev => prev.filter(id => id !== updatedGuide.id));

					// Show toast notification
					showToast(updatedGuide.id);
				}
			});
		}
	});

	const [propertiesOptions, propertyDropdownLabel = "Property"] = useMemo(() => {
		if (loggedUserValue?.user_properties?.length) {
			let selectedPropertyName;

			const productPropertiesValues = loggedUserValue?.user_properties.map(up => up.property);

			const areAllOrNonePropertiesSelected =
				selectedPropertiesFilter.length === productPropertiesValues.length || selectedPropertiesFilter.length === 0;

			if (productPropertiesValues?.length) {
				const productProperties: MenuProps["items"] = productPropertiesValues.reduce(
					(options, property, currentindex) => {
						const optionId = `property-dropdown-option-${currentindex + 1}-${property?.id}`;

						const isSelected = selectedPropertiesFilter.includes(property?.id);

						if (!selectedPropertyName && isSelected) {
							// property should be defined by this step, if not, maybe refactor to ensure it is
							selectedPropertyName = property?.name;
						}

						const newOption = {
							key: `${property?.id}`,
							icon: (
								<div>
									<input
										className="custom-checkbox"
										id={optionId}
										type="checkbox"
										onClick={e => e.stopPropagation()}
										checked={isSelected}
										onChange={voidFnc}
									/>
									<label htmlFor={optionId} className="custom-checkbox-label">
										{property?.name}
									</label>
								</div>
							)
						};

						options.push(newOption);

						return options;
					},
					[
						{
							key: "property-dropdown-option-all",
							icon: (
								<div>
									<input
										className="custom-checkbox"
										id="property-dropdown-option-all"
										type="checkbox"
										onClick={e => e.stopPropagation()}
										checked={areAllOrNonePropertiesSelected}
										onChange={voidFnc}
									/>
									<label htmlFor="property-dropdown-option-all" className="custom-checkbox-label">
										All properties
									</label>
								</div>
							)
						}
					]
				);

				return [productProperties, createLabelForFilters("Property", selectedPropertiesFilter, selectedPropertyName)];
			}
		}

		return [[], "Property"];
	}, [selectedPropertiesFilter, loggedUserValue.user_properties]);

	const [categoriesOptions, categoryDropdownLabel = "Category"] = useMemo(() => {
		if (productCategories?.size) {
			const selectedValues = selectedCategoriesFilter.map(v => productCategories.get(v));
			const productCategoryOptions: MenuProps["items"] = [
				{
					key: "category-dropdown-option-all",
					icon: (
						<div>
							<input
								className="custom-checkbox"
								id="category-dropdown-option-all"
								type="checkbox"
								onClick={e => e.stopPropagation()}
							/>
							<label htmlFor="category-dropdown-option-all" className="custom-checkbox-label">
								All Categories
							</label>
						</div>
					)
				}
			];

			productCategories.forEach((value, key) => {
				const optionId = `category-dropdown-option-${key}`;
				const isSelected = selectedCategoriesFilter?.includes(key);

				const newOption = {
					key: key,
					icon: (
						<div>
							<input
								className="custom-checkbox"
								id={optionId}
								type="checkbox"
								onClick={e => e.stopPropagation()}
								checked={isSelected}
								onChange={voidFnc}
							/>
							<label htmlFor={optionId} className="custom-checkbox-label">
								{value}
							</label>
						</div>
					)
				};

				productCategoryOptions.push(newOption);
			});

			return [productCategoryOptions, createLabelForFilters("Category", selectedValues)];
		}

		return [[], "Category"];
	}, [productCategories, selectedCategoriesFilter]);

	if (loading) {
		return <div>Loading...</div>;
	}

	return (
		<div className="products-screen-container">
			<span className="page-title">{"My Guides"}</span>
			<div className="products-content-container" ref={scrollContainerRef}>
				<div className="products-filter-group">
					<div className="search-products-row">
						<div className="products-search-container">
							<Input
								className="product-search-input-wrapper"
								name="searchProduct"
								placeholder="Search..."
								classNames={{ input: "product-search-input" }}
								value={searchTerm} // Use Zustand search term
								autoComplete="off"
								onChange={handleSearchTermChange}
								prefix={<HiSearch color={"#67748E"} size={"16px"} />}
								allowClear
							/>
						</div>
					</div>
					<div className="products-filters-and-create-guide-row">
						<div className="products-filters-row">
							<Dropdown
								menu={{
									items: propertiesOptions,
									selectable: true,
									multiple: true,
									onSelect: args => handleOnSelectPropertiesFilter(args),
									onDeselect: args => handleOnSelectPropertiesFilter(args),
									selectedKeys: selectedPropertiesFilter
								}}
								arrow
								className="products-dropdown"
								overlayClassName="products-dropdown-overlay"
								trigger={["click"]}
								open={propertiesDropdownOpen}
								onOpenChange={(open: boolean, info: { source: "trigger" | "menu" }) => {
									if (info.source === "trigger" && open === false) {
										setPropertiesDropdownOpen(false);
									}
								}}
								disabled={propertiesOptions.length === 0}
							>
								<Button
									onClick={() => {
										setPropertiesDropdownOpen(previousValue => !previousValue);
									}}
								>
									{propertyDropdownLabel}
									<img
										src={process.env.PUBLIC_URL + "/assets/images/icons/black-chevron-down.svg"}
										style={{ width: "1em", height: "1em" }}
										title={propertyDropdownLabel}
										className="chevron-button"
									/>
								</Button>
							</Dropdown>
							<Dropdown
								menu={{
									items: categoriesOptions,
									selectable: true,
									multiple: true,
									onSelect: args => handleOnSelectCategoriesFilter(args),
									onDeselect: args => handleOnSelectCategoriesFilter(args),
									selectedKeys: selectedCategoriesFilter,
									defaultSelectedKeys: selectedCategoriesFilter
								}}
								arrow
								className="products-dropdown"
								trigger={["click"]}
								open={categoriesDropdownOpen}
								onOpenChange={(open: boolean, info: { source: "trigger" | "menu" }) => {
									if (info.source === "trigger" && open === false) {
										setCategoriesDropdownOpen(false);
									}
								}}
								disabled={categoriesOptions.length === 0}
							>
								<Button
									onClick={() => {
										setCategoriesDropdownOpen(previousValue => !previousValue);
									}}
								>
									{categoryDropdownLabel}
									<img
										src={process.env.PUBLIC_URL + "/assets/images/icons/black-chevron-down.svg"}
										style={{ width: "1em", height: "1em" }}
										title={categoryDropdownLabel}
										className="chevron-button"
									/>
								</Button>
							</Dropdown>
						</div>
						<Link to={routeNames.GUIDE_ADD} className="highnote-button">
							<img src={`${process.env.PUBLIC_URL}/assets/images/icons/twinkle-star.svg`} alt="star" />
							Create a Guide
						</Link>
					</div>
				</div>
				<div className="products-table">
					<Row className="products-grid-container" align="stretch" gutter={[24, 24]}>
						{guides.map(guide => {
							return (
								<GuideCard
									key={`guide-${guide.id}`}
									onClick={() => handleOnCardClick(guide.id, guide.status)}
									guide={guide}
								/>
							);
						})}
					</Row>
				</div>
				<ScrollToTopButton parentRef={scrollContainerRef} />
				{contextHolder}
			</div>
		</div>
	);
};

export default GuidesScreen;
